import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@hipotecar/ui/lib/utils";

const alertVariants = cva(
	"relative w-full rounded-lg border p-4 [&>svg]:absolute [&>svg]:top-4 [&>svg]:left-4 [&>svg]:text-foreground [&>svg+div]:translate-y-[-3px] [&>svg~*]:pl-7",
	{
		variants: {
			variant: {
				default: "bg-background text-foreground",
				destructive:
					"border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
				success:
					"border-green-500/50 bg-green-50 text-green-900 dark:border-green-500/20 dark:bg-green-500/10 dark:text-green-400 [&>svg]:text-green-600 dark:[&>svg]:text-green-400",
				warning:
					"border-yellow-500/50 bg-yellow-50 text-yellow-900 dark:border-transparent dark:bg-yellow-400/[0.09] dark:text-yellow-400 [&>svg]:text-yellow-600 dark:[&>svg]:text-yellow-400",
				info: "border-sky-500/50 bg-sky-50 text-sky-900 dark:border-transparent dark:bg-sky-400/[0.09] dark:text-sky-400 [&>svg]:text-sky-600 dark:[&>svg]:text-sky-400",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

const Alert = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
	<div
		ref={ref}
		role="alert"
		className={cn(alertVariants({ variant }), className)}
		{...props}
	/>
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h5
		ref={ref}
		className={cn("mb-1 leading-none font-medium tracking-tight", className)}
		{...props}
	/>
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn("text-sm [&_p]:leading-relaxed", className)}
		{...props}
	/>
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
